import IBloc from '../../base/IBloc';
import { Banner, BannerViewState, CreateBannerDto, UpdateBannerDto } from '../../types/banner.types';
import { BannerService } from '../../service/supabase/BannerService';

export class BlocManagementBanners extends IBloc {
    private service: BannerService;

    /**
     * State chính của Banner Management
     */
    info: BannerViewState = {
        banners: [],
        pagination: {
            total: 0,
            page: 1,
            limit: 10,
            totalPages: 0
        },
        loading: false,
        error: null,
        filters: {},
        hasData: false
    };

    // Keep track of the current pagination settings
    currentPagination = {
        page: 1,
        limit: 10
    };

    // Keep track of the current filters
    currentFilters: Record<string, any> = {};

    constructor() {
        super();
        this.service = BannerService.getInstance();
    }

    /**
     * Initialize data
     */
    initData() {
        this.setStream("info", this.info);
        this.loadBanners();
    }

    /**
     * Load banners with current pagination and filters
     */
    private async loadBanners() {
        try {
            this.info.loading = true;
            this.setStream("info", this.info);

            const { data, pagination } = await this.service.getBanners(
                this.currentPagination.page,
                this.currentPagination.limit,
                this.currentFilters
            );

            this.info = {
                ...this.info,
                banners: data,
                pagination,
                loading: false,
                error: null,
                hasData: data.length > 0
            };
            
            this.setStream("info", this.info);
        } catch (error: any) {
            this.info = {
                ...this.info,
                loading: false,
                error: error.message || 'Failed to load banners'
            };
            
            this.setStream("info", this.info);
        }
    }

    /**
     * Set loading state
     */
    private setLoading(isLoading: boolean) {
        this.info = {
            ...this.info,
            loading: isLoading
        };
        
        this.setStream("info", this.info);
    }

    /**
     * Filter banners by title
     */
    async filterByTitle(title: string) {
        this.currentFilters.title = title || undefined;
        this.currentPagination.page = 1; // Reset to first page
        await this.loadBanners();
    }

    /**
     * Change page
     */
    async changePage(page: number) {
        this.currentPagination.page = page;
        await this.loadBanners();
    }

    /**
     * Change page size
     */
    async changePageSize(limit: number) {
        this.currentPagination = {
            page: 1, // Reset to page 1 when changing page size
            limit
        };
        await this.loadBanners();
    }

    /**
     * Clear all filters
     */
    async clearFilters() {
        this.currentFilters = {};
        this.currentPagination.page = 1; // Reset to first page
        await this.loadBanners();
    }

    /**
     * Refresh the banner list
     */
    async refresh() {
        await this.loadBanners();
    }

    /**
     * Create a new banner
     */
    async createBanner(data: CreateBannerDto): Promise<Banner> {
        try {
            this.setLoading(true);

            // If no index provided, get next available index
            if (data.index === undefined) {
                data.index = await this.service.getMaxIndex();
            }

            const banner = await this.service.createBanner(data);
            
            // Refresh banner list after creation
            await this.loadBanners();
            
            return banner;
        } catch (error: any) {
            this.info = {
                ...this.info,
                loading: false,
                error: error.message || 'Failed to create banner'
            };
            this.setStream("info", this.info);
            throw error;
        }
    }

    /**
     * Update an existing banner
     */
    async updateBanner(id: string, data: UpdateBannerDto): Promise<Banner> {
        try {
            this.setLoading(true);
            
            const banner = await this.service.updateBanner(id, data);
            
            // Refresh banner list after update
            await this.loadBanners();
            
            return banner;
        } catch (error: any) {
            this.info = {
                ...this.info,
                loading: false,
                error: error.message || 'Failed to update banner'
            };
            this.setStream("info", this.info);
            throw error;
        }
    }

    /**
     * Delete a banner
     */
    async deleteBanner(id: string): Promise<boolean> {
        try {
            this.setLoading(true);
            
            const success = await this.service.deleteBanner(id);
            
            // Refresh banner list after deletion
            await this.loadBanners();
            
            return success;
        } catch (error: any) {
            this.info = {
                ...this.info,
                loading: false,
                error: error.message || 'Failed to delete banner'
            };
            this.setStream("info", this.info);
            throw error;
        }
    }

    /**
     * Update a banner's index/order
     */
    async updateBannerIndex(id: string, index: number): Promise<Banner> {
        try {
            this.setLoading(true);
            
            const banner = await this.service.updateBannerIndex(id, index);
            
            // Refresh banner list after index update
            await this.loadBanners();
            
            return banner;
        } catch (error: any) {
            this.info = {
                ...this.info,
                loading: false,
                error: error.message || 'Failed to update banner index'
            };
            this.setStream("info", this.info);
            throw error;
        }
    }

    /**
     * Clean up any subscriptions
     */
    dispose() {
        // No need to unsubscribe from BehaviorSubject,
        // but if we had any other subscriptions, we'd clean them up here
    }
}
